import { useCallback, useEffect, useState } from "react";
import { FileText, Eye, Upload, CheckCircle, Clock } from "lucide-react";
import {
  getMyLifecycle,
  uploadMyOnboardingDocument,
  formatLifecycleError,
  getLifecycleDocumentUrl,
  getLifecycleDocumentName,
} from "./lifecycleApi";

const STATUS_STYLES = {
  Draft: "bg-slate-100 text-slate-700",
  "Pending Approval": "bg-amber-100 text-amber-800",
  Approved: "bg-blue-100 text-blue-800",
  "In Progress": "bg-violet-100 text-violet-800",
  Completed: "bg-emerald-100 text-emerald-800",
};

export default function MyOnboarding() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploadingId, setUploadingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getMyLifecycle();
      setData(res.onboarding || null);
    } catch (e) {
      setError(formatLifecycleError(e, "Failed to load"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpload = async (documentId, file) => {
    if (!data?.onboarding_id) return;
    setUploadingId(documentId);
    try {
      await uploadMyOnboardingDocument(data.onboarding_id, documentId, file);
      await load();
    } catch (e) {
      setError(formatLifecycleError(e, "Upload failed"));
    } finally {
      setUploadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="w-full max-w-full min-w-0 p-6">
        <p className="text-sm text-slate-500">Loading your onboarding…</p>
      </div>
    );
  }

  return (
    <div className="w-full max-w-full min-w-0 p-6 space-y-5">
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">My Onboarding</h1>
          <p className="text-sm text-slate-500">Upload documents and track your joining progress</p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>
      )}

      {!data ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <p className="font-medium text-slate-800">No active onboarding</p>
          <p className="text-sm text-slate-500 mt-1">
            You are fully onboarded, or HR has not started your onboarding yet.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <span className={`inline-flex mt-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[data.status] || "bg-slate-100"}`}>
                  {data.status}
                </span>
              </div>
              {data.date_of_joining && (
                <div className="text-right">
                  <p className="text-sm text-slate-500">Joining date</p>
                  <p className="font-medium text-slate-800">{data.date_of_joining}</p>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Documents</p>
                <p className="text-lg font-semibold text-slate-800">
                  {data.documents?.done ?? 0}/{data.documents?.total ?? 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Tasks</p>
                <p className="text-lg font-semibold text-slate-800">
                  {data.tasks?.done ?? 0}/{data.tasks?.total ?? 0}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
              <h2 className="font-semibold text-slate-800">Documents to upload</h2>
              <p className="text-xs text-slate-500 mt-0.5">Mandatory items are marked with *</p>
            </div>
            <div className="divide-y divide-slate-100">
              {(data.documents_list || []).map((doc) => {
                const fileName = getLifecycleDocumentName(doc);
                const fileUrl = getLifecycleDocumentUrl(doc.document_url);
                const hasFile = Boolean(fileUrl && fileName);
                const isUploading = uploadingId === doc.document_id;

                return (
                  <div key={doc.document_id} className="px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm text-slate-800">
                        {doc.document_type}
                        {doc.is_mandatory && <span className="text-red-500 ml-0.5">*</span>}
                      </p>
                      {hasFile && (
                        <div className="flex items-center gap-2 mt-1">
                          <FileText className="w-3.5 h-3.5 text-violet-500" />
                          <span className="text-xs text-slate-600 truncate">{fileName}</span>
                          <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-600 inline-flex items-center gap-0.5">
                            <Eye className="w-3 h-3" /> View
                          </a>
                        </div>
                      )}
                      {doc.status === "Rejected" && doc.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">Rejected: {doc.rejection_reason}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          doc.status === "Verified"
                            ? "bg-emerald-100 text-emerald-800"
                            : doc.status === "Uploaded"
                              ? "bg-blue-100 text-blue-800"
                              : doc.status === "Rejected"
                                ? "bg-red-100 text-red-800"
                                : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {doc.status}
                      </span>
                      <label
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-medium cursor-pointer ${
                          isUploading ? "bg-slate-100 text-slate-500" : "bg-violet-50 text-violet-700 hover:bg-violet-100"
                        }`}
                      >
                        <Upload className="w-3.5 h-3.5" />
                        {isUploading ? "Uploading…" : hasFile ? "Replace" : "Upload"}
                        <input
                          type="file"
                          className="hidden"
                          disabled={isUploading}
                          accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (file) handleUpload(doc.document_id, file);
                          }}
                        />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {(data.tasks_list || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 bg-slate-50">
                <h2 className="font-semibold text-slate-800">Onboarding tasks</h2>
                <p className="text-xs text-slate-500 mt-0.5">Tracked by HR — view only</p>
              </div>
              <div className="divide-y divide-slate-100">
                {data.tasks_list.map((task) => (
                  <div key={task.task_id} className="px-5 py-3 flex justify-between items-center text-sm">
                    <div>
                      <p className="font-medium text-slate-800">{task.title}</p>
                      <p className="text-xs text-slate-500">{task.category}</p>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        task.status === "Completed" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"
                      }`}
                    >
                      {task.status === "Completed" ? (
                        <span className="inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Done</span>
                      ) : (
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {task.status}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
